import React from "react";
import { useTranslation } from "react-i18next";
import { cn } from "@/shared/lib/cn";
import { formatBytes } from "../model/formatBytes";
import { OTHERS_KEY, formatShare, type CompositionSegment } from "../model/composition";

/**
 * Barra empilhada da composição por área, com legenda. A legenda dá nome e valor a cada cor,
 * então a identidade nunca depende só da cor; o `title` de cada fatia repete o detalhe no hover.
 */
export function UsageCompositionBar({ segments, size = "md" }: { segments: CompositionSegment[]; size?: "sm" | "md" }) {
    const { t, i18n } = useTranslation("common");
    if (segments.length === 0) return null;

    const label = (key: string) => (key === OTHERS_KEY ? t("fileUsage.others") : t(`fileUsage.areas.${key}`, { defaultValue: key }));

    return (
        <figure className="space-y-2.5">
            <div className={cn("flex w-full gap-0.5", size === "md" ? "h-3" : "h-2")} role="img" aria-label={t("fileUsage.compositionLabel")}>
                {segments.map((segment, index) => (
                    <div
                        key={segment.key}
                        title={`${label(segment.key)}: ${formatBytes(segment.bytes, i18n.language)} (${formatShare(segment.share, i18n.language)})`}
                        className={cn(index === 0 && "rounded-l", index === segments.length - 1 && "rounded-r")}
                        style={{ flexGrow: segment.share, flexBasis: 0, minWidth: 4, backgroundColor: segment.color }}
                    />
                ))}
            </div>
            <figcaption>
                <ul className="flex flex-wrap gap-x-4 gap-y-1.5 text-xs text-slate-600 dark:text-zinc-400">
                    {segments.map((segment) => (
                        <li key={segment.key} className="inline-flex items-center gap-1.5">
                            <span aria-hidden className="h-2 w-2 shrink-0 rounded-sm" style={{ backgroundColor: segment.color }} />
                            <span>{label(segment.key)}</span>
                            <span className="tabular-nums">{formatShare(segment.share, i18n.language)}</span>
                        </li>
                    ))}
                </ul>
            </figcaption>
        </figure>
    );
}
